<script>
import { mapActions } from 'vuex';
import urlParser from '../../../utils/urlParser';
import snackbarMixin from '../../../mixins/snackbar';

export default {
  name: 'FieldVisibility',
  mixins: [snackbarMixin],
  props: {
    path: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      hasError: false,
      fields: [],
    };
  },
  computed: {
    domain() {
      const { params } = urlParser('/field-visibility/:section', this.path);

      return params.section;
    },
    subTitle() {
      const titles = {
        material: 'material details',
      };

      return titles[this.domain] || '';
    },
    groupedFields() {
      const groups = {};

      this.fields.forEach((field) => {
        const {
          fieldsetCode, isDisplayed, labelCode, id,
        } = field;

        groups[fieldsetCode] = groups[fieldsetCode] || {
          fieldsetCode,
          isDisplayed: false,
          isIndeterminate: false,
          items: [],
        };

        groups[fieldsetCode].items.push({ labelCode, isDisplayed, id });
      });

      this.markGroupVisibility(groups);

      return groups;
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    ...mapActions('settings', ['fetchFieldVisibility', 'saveFieldVisibility']),
    fetch() {
      this.isLoading = true;
      this.hasError = false;

      return this.fetchFieldVisibility(this.domain)
        .then(({ data }) => {
          this.fields = data;
        })
        .catch(() => {
          this.hasError = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    markGroupVisibility(groupedFields) {
      Object.keys(groupedFields).forEach((key) => {
        const { items } = groupedFields[key];
        const isAllChecked = items.every(item => item.isDisplayed);
        const isAnyChecked = items.find(item => item.isDisplayed);

        groupedFields[key].isDisplayed = isAllChecked; // eslint-disable-line no-param-reassign
        groupedFields[key].isIndeterminate = isAnyChecked && !isAllChecked; // eslint-disable-line no-param-reassign
      });
    },
    updateVisibility(item, isGroup) {
      this.fields.forEach((field) => {
        const identifier = isGroup ? 'fieldsetCode' : 'id';

        if (field[identifier] === item[identifier]) {
          field.isDisplayed = item.isDisplayed; // eslint-disable-line no-param-reassign
        }
      });
    },
    save() {
      const { domain, fields } = this;
      this.isSaving = true;

      return this.saveFieldVisibility({ fields, domain })
        .then(() => {
          this.showSuccessMessage('Field preferences are successfully saved!');
        })
        .catch(() => {
          this.showErrorMessage('Failed to save field preferences. Please try again!');
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
  },
};
</script>

<template>
  <v-container fluid pa-0 grid-list-lg>
    <v-progress-linear v-if="isLoading" :indeterminate="true" />
    <h4 v-else-if="hasError" class="js-error-state ma-3">Unable to fetch field configuration.</h4>

    <v-layout v-else row wrap>
      <v-toolbar dark dense height="40" color="blue-grey lighten-2 ma-2">
        <v-toolbar-title>
          Configure {{ subTitle }} field visibility
        </v-toolbar-title>

        <v-spacer />

        <v-tooltip bottom>
          <v-btn icon slot="activator" disabled>
            <v-icon>search</v-icon>
          </v-btn>
          <span>Coming soon!</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn @click="fetch" icon slot="activator">
            <v-icon>refresh</v-icon>
          </v-btn>
          <span>Aktualisieren</span>
        </v-tooltip>

        <v-tooltip bottom>
          <v-btn @click="save" icon slot="activator" :loading="isSaving">
            <v-icon>save</v-icon>
          </v-btn>
          <span>Speichern</span>
        </v-tooltip>
      </v-toolbar>

      <v-flex
        v-for="group in groupedFields"
        :key="group.id"
        class="field-group"
        xs12
        sm6
      >
        <v-card>
          <v-card-text>
            <v-checkbox
              v-model="group.isDisplayed"
              :indeterminate="group.isIndeterminate"
              :label="$t(group.fieldsetCode)"
              @change="updateVisibility(group, true)"
              hide-details
              class="title"
            />
            <v-checkbox
              v-for="item in group.items"
              :key="item.labelCode"
              v-model="item.isDisplayed"
              @change="updateVisibility(item)"
              :label="$t(item.labelCode)"
              hide-details
              class="ml-4"
            />
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<style lang="scss" scoped>
.v-input--checkbox.title {
  margin-top: 0 !important;
}

.field-group .v-card {
  height: 100%;
}

.v-toolbar__title {
  font-size: 16px;
}
</style>
